'use client';
import { forwardRef, useRef, useId } from 'react';
import clsx from 'clsx';
import type { UseFormResetField } from 'react-hook-form';

import { mergeRefs } from '../../../utils';

import { Alert } from '../../alert/Alert';

export type RadioSize = 'sm' | 'lg';

export type RadioProps = {
  ref?: React.Ref<HTMLInputElement>;
  name: string;
  size?: RadioSize;
  label?: React.ReactNode;
  value: string | number | boolean;
  suppressErrorMessage?: boolean;
  subLabel?: React.ReactNode;
  error?: string;
  resetField?: UseFormResetField<Record<string, string>>;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;

export const Radio = forwardRef(function Radio(
  {
    name,
    size = 'lg',
    label,
    suppressErrorMessage,
    subLabel,
    error,
    resetField,
    disabled,
    className,
    ...props
  }: RadioProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <>
      <div
        className={clsx(
          'flex',
          'flex-row-reverse',
          'items-center',
          'justify-end',
          className
        )}
      >
        {label && (
          <label
            htmlFor={props.id || id}
            className={clsx('cap-base', 'text-neutral-80')}
          >
            <span>{label}</span>
            {subLabel && (
              <span
                className={clsx('block', 'mt-1', 'cap-xs', 'text-neutral-70')}
              >
                {subLabel}
              </span>
            )}
          </label>
        )}
        <span
          className={clsx(
            'relative',
            'flex',
            'justify-center',
            'items-center',
            'self-start',
            {
              'mr-3': !!label,
              'h-6 min-h-[1.5rem] w-6 min-w-[1.5rem]': size === 'lg',
              'h-4 min-h-[1rem] w-4 min-w-[1rem]': size === 'sm',
            }
          )}
        >
          <input
            ref={mergeRefs(ref, inputRef)}
            id={id}
            name={name}
            type="radio"
            aria-invalid={!!error}
            disabled={disabled}
            className={clsx(
              'absolute',
              'top-0',
              'left-0',
              'w-full',
              'h-full',
              'appearance-none',
              'rounded-full',
              'bg-neutral-0',
              'border-1.5',
              'border-neutral-80',
              'outline-none',
              'checked:bg-neutral-80',
              'focus:ring-2 focus:ring-offset-2 focus:ring-focus',
              'active:ring-2 active:ring-offset-2 active:ring-focus',
              'disabled:checked:bg-neutral-50 disabled:border-neutral-50',
              {
                ['hover:border-neutral-70 hover:checked:bg-neutral-70']:
                  !disabled && !error,
                ['border-neutral-70 ring-2 ring-offset-2 ring-warning-50']:
                  !!error,
              },
              'peer'
            )}
            {...props}
          />
          <span
            className={clsx(
              'relative',
              'bg-neutral-0',
              'rounded-full',
              'pointer-events-none',
              {
                'w-3 h-3': size === 'lg',
                'w-2 h-2': size === 'sm',
              },
              'opacity-0',
              'peer-checked:opacity-100'
            )}
          />
        </span>
      </div>
      {error && !suppressErrorMessage && (
        <Alert type="warning" message={error} inline={true} />
      )}
    </>
  );
});

export default Radio;
